
import { AxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { UseFormRegister, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { ERROR_MESSAGES, MAX_SMALL_MOBILE_WIDTH } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { IApiErrors, IExtractedRibInfo, IUploadedDocument } from '../../context/model';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import LoaderModal from '../../modals/common/LoaderModal';
import UploadFilePreviewModal from '../../modals/common/UploadFilePreviewModal';
import { deleteDocument, uploadRib, downloadUploadedDocument } from "../../services/CustomerApiService";
import { accessJsonAttr, getCompressFile, getFilename, isAcceptedFileSize, isValidFileSize, isValidFileType } from '../../utils/utils';
import QrCodeModal from '../../modals/common/QrCodeModal';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import ListOfFileUploaded from '../common/ListOfFileUploaded';
import MgpInputFileUpload from '../../elements/MgpInputFileUpload/MgpInputFileUpload';
import UploadFromPhoneButton from '../common/UploadFromPhoneButton';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';
import UploadDocumentNotice from '../common/UploadDocumentNotice';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';

export type InfoBankFormData = {
  iban: string;
  bankingHolder: string;
  attachRibId: string | null;
  ribHolderConfirm: boolean;
}

type InfoBankTemplateProps = {
    register: UseFormRegister<any>,
    setValue: UseFormSetValue<any>,
    errors: any,
    reset: UseFormReset<any>
}

export default function InfoBankTemplate({ register, errors, setValue, reset}: Readonly<InfoBankTemplateProps>) {

    const {
        state: { proposition, ribDocument, jwtToken },
        dispatch,
      } = useAppState();
    
      const [errorMsg, setErrorMsg] = useState<string | null>();
      const [errorExtractRibMsg, setErrorExtractRibMsg] = useState<string | null>();
      const [errorFileTypeMsg, setErrorFileTypeMsg] = useState<boolean | null>();
      const [errorFileSizeMsg, setErrorFileSizeMsg] = useState<boolean | null>();
      const [loading, setLoading] = useState<boolean>(false);
      const [file, setFile] = useState<File | null>(null);
      const [openPreviewFile, setOpenPreviewFile] = useState<boolean>(false);
      const [isShowModal, setIsShowModal] = useState<boolean>(false);
      const [showConfirmUploadButton, setShowConfirmUploadButton] = useState<boolean>(false);
    
      const {width} = useWindowDimensions();
      const isDesktop = width > MAX_SMALL_MOBILE_WIDTH;
     
      const showQrCodeModal = () => {
        setIsShowModal(true);
      }
      const closeModal = () => {
        setIsShowModal(false);
      }
     
    
      const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        try {
          const uploadedfile = event.target.files ? event.target.files[0] : null;
          if (uploadedfile) {
            if (!isValidFileType(uploadedfile)) {
              setErrorFileTypeMsg(true)
              setErrorExtractRibMsg(null)
            } else if (!isValidFileSize(uploadedfile)) {
              setErrorFileSizeMsg(true)
              setErrorExtractRibMsg(null)
            } else if (!isAcceptedFileSize(uploadedfile)) {
              try {
                const token = jwtToken!.accessToken;
                const newFile = await getCompressFile(token, uploadedfile);
                setFile(newFile);
                setOpenPreviewFile(true);
                setShowConfirmUploadButton(true);
                dispatch({type: 'SHOW_MODAL', showModal: true});
              } catch(e) {
                setErrorMsg("Suite à un incident technique, nous ne pouvons pas uploader votre fichier. Veuillez sélectionner à nouveau.");
              }  
            } else {
              setFile(uploadedfile);
              setOpenPreviewFile(true);
              setShowConfirmUploadButton(true);
              dispatch({type: 'SHOW_MODAL', showModal: true});
            }
          }
        } catch (error) {
          const err = error as AxiosError;
          console.log(err);
          if(err.response && err.response.status === 400) {
            const errorData = err.response?.data as IApiErrors;
            setErrorMsg("Erreur: " + errorData.message);
          } else {
            setErrorMsg("Une erreur technique est survenue ");
          }
        }
      
      };
    
      const onUploadAction = async () => {
        setErrorMsg(null);
        setErrorExtractRibMsg(null);
        setErrorFileTypeMsg(false);
        setErrorFileSizeMsg(false);
        setLoading(true);
        try {
          const token = jwtToken!.accessToken;
          const res = await uploadRib(token, proposition!.propositionId, file as File);
          setLoading(false);
          const ribDocument = res.data;
          onUpdatedFormValue(ribDocument);
        } catch (error) {
          setLoading(false);
          const err = error as AxiosError;
          console.log(err);
          if(err.response && err.response.status === 400) {
            const errorData = err.response?.data as IApiErrors;
            setErrorMsg("Erreur: " + errorData.message);
          } else {
            setErrorMsg("Une erreur technique est survenue ");
          }
        }
      }
    
      const onEventMessage = (message: any, type: string) => {
        if(type === 'upload') {
          const ribDocument = message as IUploadedDocument<IExtractedRibInfo>;
          onUpdatedFormValue(ribDocument);
          setIsShowModal(false);
          setShowConfirmUploadButton(false);
        }
      }
    
      const onUpdatedFormValue = (ribDocument: IUploadedDocument<IExtractedRibInfo>) => {
        reset({
          bankingHolder: '',
          attachRibId: '',
          iban:'',
          ribHolderConfirm: ''
        })
        if(ribDocument?.extractedData.isValid === true) {
          setValue('bankingHolder', ribDocument?.extractedData.accountOwner, { shouldValidate: true });
          setValue('iban', ribDocument?.extractedData.iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim(), { shouldValidate: true });
        } else if(ribDocument?.extractedData.isValid === false){
          setErrorExtractRibMsg("Nous n’avons pas réussi à identifier votre document. Veuillez compléter les informations suivantes pour poursuivre.")
        } else if(ribDocument?.extractedData.iban === null || ribDocument?.extractedData?.accountOwner?.length === 0 ){
          setErrorExtractRibMsg("«Nous n’avons pas réussi à identifier toutes vos données. Veuillez compléter les informations suivantes pour poursuivre.")
        }
        setValue('attachRibId', ribDocument.id);
        dispatch({type: 'EXTRACT_RIB', ribDocument});
      }

      const downloadDocument = async (documentId: string) => {
        try {
          const token = jwtToken!.accessToken;
          const res = await downloadUploadedDocument(token, proposition!.propositionId, documentId);
          const filename = getFilename(res.headers);
          const file = new File([res.data], filename);
          setFile(file);
          setOpenPreviewFile(true);
          dispatch({type: 'SHOW_MODAL', showModal: true});
          setShowConfirmUploadButton(false);
    
        } catch (error) {
          setLoading(false);
    
          const err = error as AxiosError;
          console.log(err);
          if(err.response && err.response.status === 400) {
            const errorData = err.response?.data as IApiErrors;
            setErrorMsg("Erreur: " + errorData.message);
          } else if (err.response && err.response.status === 404) {
            setErrorMsg(ERROR_MESSAGES.documentNotFound);
          } else {
            setErrorMsg("Une erreur technique est survenue ");
          }
        }
      }

      const onDeleteDocLocal = () => {
        dispatch({type: 'EXTRACT_RIB', ribDocument: null});
        setLoading(false);
        reset({
          bankingHolder: '',
          attachRibId: '',
          iban:'',
          ribHolderConfirm: ''
        })
        setErrorExtractRibMsg(null);
        setErrorMsg(null);
      }
    
      const onDeleteDocument = async (documentId: string) => {
        setLoading(true);
        try {
          const token = jwtToken!.accessToken;
          await deleteDocument(token, proposition!.propositionId, documentId);
          
          onDeleteDocLocal();
    
        } catch (error) {
          const err = error as AxiosError;
          console.log(err);
          if(err.response && err.response.status === 400) {
            const errorData = err.response?.data as IApiErrors;
            setErrorMsg("Erreur: " + errorData.message);
          } else if (err.response && err.response.status === 404) {
            onDeleteDocLocal();
          } else {
            setErrorMsg("Une erreur technique est survenue ");
          }
          setLoading(false);

        }
        
      }
    
    
      const displayRibInfo = () => {
        return (<div>
          <div className="">
            <div className='form-row'>
              <div className="form-group mw-100">
                <MgpFormInput
                  label="Titulaire du compte"
                  id="bankingHolder"
                  labelFontWeight='regular'
                  required
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
            
            <div className='form-row'>
              <div className="form-group mw-100">
                <MgpFormInput
                  label="Votre IBAN"
                  id="iban"
                  labelFontWeight='regular'
                  required
                  register={register}
                  errors={errors}
                  autospace={4}
                  autoCapitalize='characters'
                />
              </div>
            </div>
          </div>
    
          {ribDocument?.extractedData && ribDocument?.extractedData.isValid &&
          <div className='mw-100'>
            <UploadDocumentNotice text="Veuillez vérifier que les informations renseignées sont correctes. N'hésitez pas à les corriger si besoin." />
          </div>}
    
          <div className="mt-xs">
            <div className="form-group form-check mw-100">
              <MgpFormCheckbox id='ribHolderConfirm' label="J'atteste sur l'honneur que je suis le titulaire du compte et qu'il ne s'agit pas d'un compte bancaire professionnel" register={register} errors={errors} required={true}/>
            </div> 
            <div className="form-row">
              {errors && accessJsonAttr(errors, 'ribHolderConfirm') && <p className="error">Veuillez cocher la case</p>}
            </div>
          </div>
    
        </div>);
      }
    
      return (
          <form style={{width: '100%'}}>  
            <div className='form-row page-content'>
              <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
                <div className=''>
                  <h2 className='card-title step-title bleu mb-sm'>Etape 1 : Je joins mon relevé d'identité bancaire</h2>
                </div>
      
                <div className='text pb-sm'>Pour nous permettre de vous rembourser vos frais de santé, nous vous invitons maintenant à télécharger votre RIB et à remplir les informations ci-dessous. </div>
               
                {errorExtractRibMsg && <p className='text bg-menthe blanc mb-sm p-sm mw-100 bg-orange'>{errorExtractRibMsg}</p>}
                {(errorFileTypeMsg || errorFileSizeMsg) &&
                  <div className='form-row'>
                    <div className='form-group mw-100'>
                      {errorFileTypeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileTypeMsg}</p> }
                      {errorFileSizeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileSizeMsg}</p> }
                    </div>
                  </div>
                }
    
                <div className="form-row">   
                  {ribDocument?.id ?
                  <div>
                    <div className="form-row">
                      <div className='form-group mw-100'>
                        <ListOfFileUploaded 
                            fileName={ribDocument.name}
                            onDownloadDocument={() => downloadDocument(ribDocument.id)}
                            onDeleteDocument={() => onDeleteDocument(ribDocument.id)}
                        />
                      </div>
                    </div>
                  </div>
                    
                    :
                    <div>
                      <p className='text mt-sm'>Téléchargez votre RIB</p>
                      <div className="form-row">
                        <div className='form-group mw-45'>
                          <MgpInputFileUpload
                            id="attachRib"
                            onChange={handleFileChange}
                            register={register} 
                            errors={errors}
                            onClick={(e)=> e.currentTarget.value = ''}
                          />
                        </div>
                        {
                          isDesktop && <div className="form-group mw-5 ta-center pt-xl"><p className='fz-lg'>ou</p></div>
                        }
                        {isDesktop && 
                          <div className="form-group mw-45">
                            <UploadFromPhoneButton onClick={() => showQrCodeModal()}/> 
                          </div>}
                      </div>
                    </div>
                  }
                </div>
                <div>
                  {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
                </div>
              </div>
    
              <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
              <div className=''>
                  <h2 className='card-title step-title bleu mb-sm'>Etape 2 : Je vérifie ou complète mes informations</h2>
                </div>
                <div className='box-subTitle'>
                  {
                    ribDocument?.extractedData?.isValid === false ?
                    <p className='text pb-sm fushia'>Vos informations n'ont pas été renseignées automatiquement, vous pouvez les compléter ici</p>
                    :
                    <p className='text pb-sm'>Vos informations ont été automatiquement renseignées à partir de votre RIB.</p>
                  }
                 
                </div>
                
                <div className='pt-md'>
                  {displayRibInfo()}
                </div>
                
    
                <div className="mt-xl">
                  <p className='text fw-medium'><span className='fushia'>(*)</span> Champs obligatoires</p>
                </div>
              </div>
            </div>
            
            {openPreviewFile&&file&&<UploadFilePreviewModal file={file} onUploadAction={onUploadAction} isOpen={openPreviewFile} onCloseModal={() => {setOpenPreviewFile(false);  dispatch({type: 'SHOW_MODAL', showModal: false});}} showButton={showConfirmUploadButton}/>}
            
            {isShowModal&&<QrCodeModal isOpen={isShowModal} onCloseModal={closeModal} docCode="RIB" onEventMessage={onEventMessage}/>}
    
    
            {loading && <LoaderModal hasMessage={false}/>}
            
          </form>
      )
}
